/**
 * @file   src\mobile\pages\ViewClientWantAd.js
 * @brief  This file is responsible for view iventories of each client section in agent dashboard
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect, useRef } from "react";
import { Tabs, Tab } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import BedSmallIcn from "../images/bed-small-icn.svg";
import BathSmallIcn from "../images/bath-small-icn.svg";
import SqftSmallIcn from "../images/sqft-small-icn.svg";
import "../styles/clients.scss";
import { getClientDetails } from "../../services/agentClientsService";
import {
  INVENTORY_KEY,
  INVENTORY_LTR,
  INVENTORY_LTR_STR,
  INVENTORY_SELL,
  INVENTORY_SELL_LTR,
  INVENTORY_SELL_STR,
  INVENTORY_STR,
} from "../../constants/common";
import {
  removeDecimalZeros,
  setLocalStorage,
  truncate,
} from "../../helpers/common";
import { pageURLs } from "../../constants/pageURLs";
import Sortoffcanvas from "../components/common/Sortoffcanvas";
import { checkInventoryType, checkValidImage } from "../../helpers/inventory";
import noImage from "../../images/no-image-placeholder.png";
import { getInventoryById } from "../../services/inventoryService";

const ViewInventoryClient = (props) => {
  useEffect(() => {
    document.body.classList.add("bg-offcanvas-fix");
  }, []);

  const location = useLocation();
  const clientId = location.state && location.state["clientId"];

  const navigate = useNavigate();
  const innerLayoutRef = useRef(null);

  const PAGE_LIMIT = 10;
  const [currPage, setCurrPage] = useState(1); // storing current page number
  const [prevPage, setPrevPage] = useState(0); // storing prev page number
  const [wasLastList, setWasLastList] = useState(false); // setting a flag to know the last list
  const [showNoResult, setShowNoResult] = useState(false);
  const state = location.state;
  const [status, setStatus] = useState(
    state && state?.showDraft ? "drafts" : "stored"
  );
  const [options, setOptions] = useState([
    { value: "createdon", label: "Recently " + status },
    { value: "price", label: "Price" },
  ]);
  const [sortParam, setSortParam] = useState(options[0]);
  const [isSortParamChanged, setIsSortParamChanged] = useState(false);
  const [myInventoryList, setMyInventoryList] = useState([]); // to set the inventory data
  const [clientDetails, setClientDetails] = useState(null); // to set the inventory data

  const [showInventorySort, setShowInventorySort] = useState(false);
  const InventorySortClose = () => setShowInventorySort(false);
  const InventorySortShow = () => setShowInventorySort(true);

  const handleSortParam = (selectedParam) => {
    setIsSortParamChanged(true);
    setSortParam(selectedParam);
    setShowInventorySort(false);
  };

  const fetchMyInventory = async (query) => {
    setShowNoResult(false);
    await getClientDetails(query)
      .then((response) => {
        const responseResult = _.get(response, "result", null);

        if (response.errorCode === 0 && responseResult.inventoryList) {
          setMyInventoryList([
            ...myInventoryList,
            ...responseResult.inventoryList,
          ]);
          setClientDetails(responseResult.clientDetails);
          // to show no data labels
          if (
            [...myInventoryList, ...responseResult.inventoryList]?.length == 0
          ) {
            setShowNoResult(true);
          } else {
            setShowNoResult(false);
          }
        } else if (
          responseResult &&
          responseResult?.inventoryList?.length === 0
        ) {
          setShowNoResult(true);
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  useEffect(() => {
    if (isSortParamChanged) {
      setCurrPage(1);
      setPrevPage(0);
      setMyInventoryList([]);
      setWasLastList(false);
      setIsSortParamChanged(false);
    }
    if (currPage == 0) {
      //setMyInventoryList([]);
    }
    if (!wasLastList && prevPage !== currPage) {
      const data = {
        clientId: clientId,
        isClientInfoReq: true,
        type: 2, // For Inventory
        sortBy: sortParam?.value,
        page: currPage,
        limit: PAGE_LIMIT,
        status: status,
      };
      fetchMyInventory(data);
    }
  }, [
    currPage,
    wasLastList,
    status,
    sortParam?.value,
    isSortParamChanged == true,
  ]);

  useEffect(() => {
    setOptions([
      { value: "createdon", label: "Recently " + status },
      { value: "price", label: "Price" },
    ]);
    setSortParam({ value: "createdon", label: "Recently " + status });
  }, [status]);

  /**
   * To handle scroll for pagination
   */
  const handleScroll = () => {
    const el = innerLayoutRef.current;
    const scrollHeight = el.scrollHeight;
    const scrollTop = el.scrollTop;
    const clientHeight = el.clientHeight;
    if (
      scrollHeight !== clientHeight &&
      scrollTop !== 0 &&
      Math.abs(scrollHeight - (scrollTop + clientHeight)) <= 1
    ) {
      setCurrPage((prevState) => prevState + 1);
    } else if (scrollTop === 0 && scrollHeight !== clientHeight) {
      //setCurrPage(prevState => prevState -1);
    }
  };
  /**
   * To handle edit inventory
   */
  const onEdit = async (inventoryId) => {
    if (inventoryId) {
      await getInventoryById(inventoryId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            navigate(pageURLs.mobileEditInventory, {
              state: {
                data: responseResult,
                inventoryId: inventoryId,
              },
            });
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };
  /**
   * To handle navigation to details page
   */
  const handleInventoryItemClick = (inventoryId) => {
    setLocalStorage(INVENTORY_KEY, {
      inventoryId: inventoryId,
      isFromClientInventoryPage: true,
      clientId: clientId,
    });
    navigate(pageURLs.mobilememberInventoryDetails);
  };

  function Inventory(props) {
    const inventory = props.inventory;
    const invType = checkInventoryType(_.get(inventory, "proprtyPurpose", ""));
    const bedRoom = removeDecimalZeros(_.get(inventory, "Bedrooms", ""));
    const bathRoom = removeDecimalZeros(_.get(inventory, "Bathrooms", ""));
    const sqFt = removeDecimalZeros(_.get(inventory, "Sqft", ""));
    const acreage = removeDecimalZeros(_.get(inventory, "TotalAcre", ""));
    let lotSizeUnit = _.get(inventory, "Acre_Sqft", "");
    if (lotSizeUnit && lotSizeUnit !== 0) {
      lotSizeUnit = lotSizeUnit === 1 ? " Acres" : " Sqft";
    }
    const isSold = _.get(inventory, "IsSold", "");
    const photos = _.get(inventory, "CoverPhotos", []);
    const firstPhoto = checkValidImage({ photos: photos, index: 0 });
    const secondPhoto = checkValidImage({ photos: photos, index: 1 });
    const thirdPhoto = checkValidImage({ photos: photos, index: 2 });
    const isDraftItem = _.get(inventory, "IsSavedDraft", "");
    const inventoryId = _.get(inventory, "Inv_Id", "");
    let titleText = "";
    switch (invType) {
      case INVENTORY_SELL:
        titleText = " selling";
        break;
      case INVENTORY_LTR:
        titleText = " renting";
        break;
      case INVENTORY_STR:
        titleText = " renting";
        break;
      case INVENTORY_LTR_STR:
        titleText = " renting";
        break;
      case INVENTORY_SELL_LTR:
        titleText = " selling, renting";
        break;
      case INVENTORY_SELL_STR:
        titleText = " selling, renting";
        break;
      default:
        titleText = " selling, renting";
        break;
    }

    return (
      <>
        <a
          href={void 0}
          role={!isDraftItem ? "button" : ""}
          className="txt-no-underline"
          onClick={() =>
            !isDraftItem
              ? handleInventoryItemClick(inventoryId)
              : onEdit(inventoryId)
          }
        >
          <div className="property-listing-mob">
            <div className="inventory-banner-small position-relative">
              {photos.length === 0 && (
                <div className="banner-row">
                  <div className="banner-column-full w-100 h-166">
                    <div className="full-col-img-wrap h-100 placeholder-wrap-bg brdr-8">
                      <img
                        className="brdr-8 placeholder-img-style"
                        src={noImage}
                        alt=""
                      />
                    </div>
                  </div>
                </div>
              )}
              {photos.length === 1 && (
                <div className="banner-row">
                  <div className="banner-column-full w-100 h-166">
                    <div className="full-col-img-wrap h-100">
                      <img
                        className="brdr-8 col-img-style"
                        src={firstPhoto}
                        alt=""
                      />
                    </div>
                  </div>
                </div>
              )}
              {photos.length === 2 && (
                <div className="banner-row">
                  <div className="inventory-banner-small banner-column-half-small h-166">
                    <div className="half-col-img-wrap h-100">
                      <img
                        className="brdr-top-left-8 brdr-btm-left-8 brdr-t-r-8 no-brdr-b-l-0 col-img-style"
                        src={firstPhoto}
                        alt=""
                      />
                    </div>
                  </div>

                  <div className="inventory-banner-small banner-column-half-small h-166">
                    <div className="half-col-img-wrap-right h-100">
                      <img
                        className="brdr-top-right-8 brdr-btm-right-8 no-brdr-t-r-0 brdr-b-r-8 brdr-b-l-8 col-img-style "
                        src={secondPhoto}
                        alt=""
                      />
                    </div>
                  </div>
                </div>
              )}
              {photos.length >= 3 && (
                <div className="banner-row">
                  <div className="banner-column-half h-166">
                    <div className="half-col-img-wrap h-100">
                      <img
                        className="brdr-top-left-8 brdr-btm-left-8 brdr-t-r-8 no-brder-b-l-0 col-img-style"
                        src={firstPhoto}
                        alt=""
                      />
                    </div>
                  </div>

                  <div className="banner-column-quarter h-166">
                    <div className="quarter-col-img-wrap-top h-50-percent">
                      <img
                        className="brdr-top-right-8 no-brdr-t-r-0 col-img-style"
                        src={secondPhoto}
                        alt=""
                      />
                    </div>
                    <div className="quarter-col-img-wrap-btm h-50-percent">
                      <img
                        className="brdr-btm-right-8 brdr-b-l-8 col-img-style"
                        src={thirdPhoto}
                        alt=""
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="property-info-outer-mob">
              <div className="property-top-mob">
                <div className="property-info-wrap-mob">
                  <div className="prize-range-badge-mob">
                    {" "}
                    {isSold ? "CLOSED" : "Open To " + titleText}
                  </div>

                  <span
                    className={
                      _.get(inventory, "Intro_Count", "0") > 0
                        ? "want-ad-tag align-middle green-tag-bg"
                        : "want-ad-tag align-middle grey-tag-bg"
                    }
                  >
                    <i className="buy-icon"></i>
                    {_.get(inventory, "Intro_Count", "0")}
                  </span>
                  <h6 className="long-txt-fix">
                    {" "}
                    {truncate(_.get(inventory, "Location", ""), 40)}
                  </h6>

                  <ul className="want-ad-btm-list">
                    {bedRoom && (
                      <li>
                        <span className="list-txt-btm">{bedRoom}</span>
                        <span className="list-icns-btm">
                          <img src={BedSmallIcn} alt="" />
                        </span>
                      </li>
                    )}
                    {bathRoom && (
                      <>
                        <li className="sep-v-line">|</li>
                        <li>
                          <span className="list-txt-btm">{bathRoom}</span>
                          <span className="list-icns-btm">
                            <img src={BathSmallIcn} alt="" />
                          </span>
                        </li>
                      </>
                    )}
                    {sqFt && (
                      <>
                        <li className="sep-v-line">|</li>
                        <li>
                          <span className="list-txt-btm">{sqFt} sqft</span>
                          <span className="list-icns-btm">
                            <img src={SqftSmallIcn} alt="" />
                          </span>
                        </li>
                      </>
                    )}
                    {acreage && (
                      <>
                        <li className="sep-v-line">|</li>
                        <li>
                          <span className="list-txt-btm">
                            {acreage + lotSizeUnit}
                          </span>
                          <span className="list-icns-btm">
                            <img src={SqftSmallIcn} alt="" />
                          </span>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
                <div className="social-btns-wrap rmt-12"></div>
              </div>
            </div>
          </div>
        </a>
      </>
    );
  }
  /**
   * No data view
   */
  function NoData(props) {
    if (showNoResult) {
      const { FirstName } = props;

      return (
        <div className="no-data d-flex align-items-center justify-content-center flex-column text-center">
          <h4>
            You don’t have any properties in your inventory for this client
          </h4>
          <p className="m-0 text-center">
            Adding {FirstName ? FirstName + "'s" : ""} properties to Gitcha will
            allow you to connect with interested buyers and renters.
          </p>
          <div className="inventory-features">
            <div className="inventory-item">
              <span className="feature-ico">
                <i className="ico-private"></i>
              </span>
              <p className="m-0">
                Private to you and those you introduce it to
              </p>
            </div>
            <div className="inventory-item">
              <span className="feature-ico">
                <i className="ico-view"></i>
              </span>
              <p className="m-0">Not a live listing on MLS</p>
            </div>
            <div className="inventory-item">
              <span className="feature-ico">
                <i className="ico-ad"></i>
              </span>
              <p className="m-0">Get matched to want-listings</p>
            </div>
            <div className="inventory-item">
              <span className="feature-ico">
                <i className="ico-buyer"></i>
              </span>
              <p className="m-0">
                Make private introductions to buyers and renters
              </p>
            </div>
            <div className="inventory-item">
              <span className="feature-ico">
                <i className="ico-message"></i>
              </span>
              <p className="m-0">Message with tools to transact</p>
            </div>
          </div>
        </div>
      );
    }
  }

  const getClientLogo = (firstName, lastName) => {
    let clientName =
      (lastName ? lastName : "") + " " + (firstName ? firstName : "");
    if (clientName) {
      clientName = clientName.replace(/-/g, "").match(/\b(\w)/g);
    }
    return clientName;
  };

  const handleTabChange = (tab) => {
    if (tab !== status) {
      setSortParam(options[0]);
      setMyInventoryList([]);
      setCurrPage(1);
      setStatus(tab);
      setPrevPage(0);
      setWasLastList(false);
    }
  };

  return (
    <div className="gitcha-main-wrapper my-client-wrap view-client-wrap">
      <div className="content-wrapper">
        <div className="inner-layout-header-new bg-white">
          <div className="top-three-comp-header w-100 top-three-comp-headerborder">
            <div className="top-three-comp-header-inner w-100">
              <a
                className="back-link-btn"
                href={void 0}
                onClick={() =>
                  navigate(pageURLs.viewClientDetailsMob, {
                    state: { clientId: clientId },
                  })
                }
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.1003 5.39972C10.3932 5.69262 10.3932 6.16749 10.1003 6.46038L5.31066 11.2501H20.5C20.9142 11.2501 21.25 11.5858 21.25 12.0001C21.25 12.4143 20.9142 12.7501 20.5 12.7501H5.31066L10.1003 17.5397C10.3932 17.8326 10.3932 18.3075 10.1003 18.6004C9.80744 18.8933 9.33256 18.8933 9.03967 18.6004L2.96967 12.5304C2.82902 12.3897 2.75 12.199 2.75 12.0001C2.75 11.8011 2.82902 11.6104 2.96967 11.4697L9.03967 5.39972C9.33256 5.10683 9.80744 5.10683 10.1003 5.39972Z"
                    fill="#1B1C1E"
                  />
                </svg>
              </a>
              <div className="heading">
                Inventory
                <div className="profile-details">
                  <div className="profile-img profile-bg-paleblue">
                    {getClientLogo(
                      clientDetails?.FirstName,
                      clientDetails?.LastName
                    )}
                  </div>
                  <div className="profile-name">
                    <p className="m-0">
                      {clientDetails
                        ? clientDetails?.FirstName +
                          " " +
                          clientDetails?.LastName
                        : ""}
                    </p>
                  </div>
                </div>
              </div>
              <div className="right-mobbtn-wrap">
                <a className="action-btn" href="#" onClick={InventorySortShow}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M10 10C10 10.1658 9.93415 10.3247 9.81694 10.4419C9.69973 10.5592 9.54076 10.625 9.375 10.625H3.75C3.58424 10.625 3.42527 10.5592 3.30806 10.4419C3.19085 10.3247 3.125 10.1658 3.125 10C3.125 9.83424 3.19085 9.67527 3.30806 9.55806C3.42527 9.44085 3.58424 9.375 3.75 9.375H9.375C9.54076 9.375 9.69973 9.44085 9.81694 9.55806C9.93415 9.67527 10 9.83424 10 10ZM3.75 5.625H14.375C14.5408 5.625 14.6997 5.55915 14.8169 5.44194C14.9342 5.32473 15 5.16576 15 5C15 4.83424 14.9342 4.67527 14.8169 4.55806C14.6997 4.44085 14.5408 4.375 14.375 4.375H3.75C3.58424 4.375 3.42527 4.44085 3.30806 4.55806C3.19085 4.67527 3.125 4.83424 3.125 5C3.125 5.16576 3.19085 5.32473 3.30806 5.44194C3.42527 5.55915 3.58424 5.625 3.75 5.625ZM8.125 14.375H3.75C3.58424 14.375 3.42527 14.4408 3.30806 14.5581C3.19085 14.6753 3.125 14.8342 3.125 15C3.125 15.1658 3.19085 15.3247 3.30806 15.4419C3.42527 15.5592 3.58424 15.625 3.75 15.625H8.125C8.29076 15.625 8.44973 15.5592 8.56694 15.4419C8.68415 15.3247 8.75 15.1658 8.75 15C8.75 14.8342 8.68415 14.6753 8.56694 14.5581C8.44973 14.4408 8.29076 14.375 8.125 14.375ZM17.9422 12.6828C17.8841 12.6247 17.8152 12.5786 17.7393 12.5472C17.6635 12.5157 17.5821 12.4995 17.5 12.4995C17.4179 12.4995 17.3365 12.5157 17.2607 12.5472C17.1848 12.5786 17.1159 12.6247 17.0578 12.6828L15 14.7414V8.75C15 8.58424 14.9342 8.42527 14.8169 8.30806C14.6997 8.19085 14.5408 8.125 14.375 8.125C14.2092 8.125 14.0503 8.19085 13.9331 8.30806C13.8158 8.42527 13.75 8.58424 13.75 8.75V14.7414L11.6922 12.6828C11.5749 12.5655 11.4159 12.4997 11.25 12.4997C11.0841 12.4997 10.9251 12.5655 10.8078 12.6828C10.6905 12.8001 10.6247 12.9591 10.6247 13.125C10.6247 13.2909 10.6905 13.4499 10.8078 13.5672L13.9328 16.6922C13.9909 16.7503 14.0598 16.7964 14.1357 16.8279C14.2115 16.8593 14.2929 16.8755 14.375 16.8755C14.4571 16.8755 14.5385 16.8593 14.6143 16.8279C14.6902 16.7964 14.7591 16.7503 14.8172 16.6922L17.9422 13.5672C18.0003 13.5091 18.0464 13.4402 18.0779 13.3643C18.1093 13.2885 18.1255 13.2071 18.1255 13.125C18.1255 13.0429 18.1093 12.9615 18.0779 12.8857C18.0464 12.8098 18.0003 12.7409 17.9422 12.6828Z"
                      fill="#1B1C1E"
                    />
                  </svg>
                </a>
                <Sortoffcanvas
                  show={showInventorySort}
                  onHide={InventorySortClose}
                  sortParam={sortParam}
                  handleSortParam={handleSortParam}
                  options={options}
                />
              </div>
            </div>
          </div>
        </div>
        <div
          className="inner-layout-content-new"
          ref={innerLayoutRef}
          onScroll={handleScroll}
        >
          <div className="tab-wrap tab-wrap-fix">
            <div className="tab-scroll-hide"></div>
            <Tabs
              id="controlled-tab-example"
              unmountOnExit
              activeKey={status}
              onSelect={(k) => {
                handleTabChange(k);
              }}
            >
              <Tab eventKey="stored" title="Stored">
                {myInventoryList !== null && myInventoryList?.length > 0 ? (
                  myInventoryList?.map((inv, i) => {
                    return <Inventory key={i} inventory={inv} ival={i} />;
                  })
                ) : (
                  <NoData FirstName={clientDetails?.FirstName} />
                )}
              </Tab>
              <Tab eventKey="closed" title="Closed">
                {myInventoryList !== null && myInventoryList?.length > 0 ? (
                  myInventoryList?.map((inv, i) => {
                    return <Inventory key={i} inventory={inv} ival={i} />;
                  })
                ) : (
                  <NoData FirstName={clientDetails?.FirstName} />
                )}
              </Tab>

              <Tab eventKey="drafts" title="Drafts">
                {myInventoryList !== null && myInventoryList?.length > 0 ? (
                  myInventoryList?.map((inv, i) => {
                    return <Inventory key={i} inventory={inv} ival={i} />;
                  })
                ) : (
                  <NoData FirstName={clientDetails?.FirstName} />
                )}
              </Tab>
            </Tabs>
          </div>
        </div>
      </div>
      <div className="inner-layout-footer-new">
        <div className="inner-btm-fixed-nav">
          <div className="btn-wrap">
            <button
              type="button"
              className="btn btn-primary w-100"
              onClick={() => navigate(pageURLs.mobileCreateInventory)}
            >
              Add New Property
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewInventoryClient;
